import { IGnsOptionsConfig } from '../../../../domain/conf/identity'
import { logger } from '../../../../utils/common/logger'
import { DOMUtils } from '../../../../utils/dom'
import { Store } from '../../../../utils/storage/keys'
import { IProfile } from '../../../common/profile'
import {
    IExtendedAccessServiceAdapter,
    IIdentityServiceAdapter,
    IIExtendedAccessUserState,
    ISubscriberServiceAdapter
} from '../adapter'

interface IWindow extends Window {
    GaaMetering: { getLoginPromise: any,
                   init: any },
    tp: any
}

interface IGaaMeteringParam  {
    allowedReferrers?: string[],
    authorizationUrl?: string,
    handleLoginPromise: Promise<any>,
    handleSwGEntitlement: () => void,
    publisherEntitlementPromise: Promise<any>,
    registerUserPromise: Promise<any>,
    showPaywall: () => void,
    unlockArticle: () => void,
    userState: IIExtendedAccessUserState
}
declare let window: IWindow

export class ExtendedAccessService implements IExtendedAccessServiceAdapter {

    public urlSwg = 'https://news.google.com/swg/js/v1/swg.js'
    public urlSwgGaa = 'https://news.google.com/swg/js/v1/swg-gaa.js'
    public urlGsiClient = 'https://accounts.google.com/gsi/client'
    public authorizationUrl: string
    public allowedReferrers: string[]
    public gnsOptions: IGnsOptionsConfig
    private subService: ISubscriberServiceAdapter
    private identityProvider: IIdentityServiceAdapter
    private readonly pianoAid: string

    constructor(authorizationUrl: string, allowedReferrers: string[], subscriberService: ISubscriberServiceAdapter,
                identityProvider: IIdentityServiceAdapter, pianoAid: string, gnsOptions: IGnsOptionsConfig) {
        const npOfferId = 'OFPI60QPGCE1'
        const npTemplateId = 'OT4PMPI615YZ'
        this.authorizationUrl = authorizationUrl
        this.allowedReferrers = allowedReferrers
        this.subService = subscriberService
        this.identityProvider = identityProvider
        this.pianoAid = pianoAid
        this.gnsOptions = gnsOptions ? gnsOptions : { offerId: npOfferId, templateId: npTemplateId }
    }

    public async init() {
        if (this.isGnsEnabled(location.href)) {
            await DOMUtils.ready
            await this.loadGoogleLibraries()
        }
    }

    public async loadGoogleLibraries() {
        DOMUtils.loadScript(this.urlSwg, [{
            key: 'subscriptions-control',
            value: 'manual'
        }])
        DOMUtils.loadScript(this.urlSwgGaa)
        DOMUtils.loadScript(this.urlGsiClient, undefined, undefined, async () => {
            try {
                await this.gnsReady()
                await this.initGaaMetering(this.authorizationUrl, this.allowedReferrers)
                logger('info', 'GNS scripts loaded')
            } catch (e) {
                logger('info', 'GNS scripts not loaded')
            }
        })
    }

    public async gnsReady() {
        return new Promise((resolve, reject) => {
            if (window.GaaMetering && window.GaaMetering.init) {
                resolve(window.GaaMetering)
            } else {
                setTimeout(() => {
                    window.GaaMetering && window.GaaMetering.init ?
                        resolve(window.GaaMetering) : reject(new Error('GNS libs'))
                    // tslint:disable-next-line:no-magic-numbers
                }, 1000)
            }
        })
    }

    public updateUserState(profile: IProfile, isMeterExpired: boolean) {
        const userState: IIExtendedAccessUserState = { granted: !isMeterExpired || profile.adLight }
        if (userState.granted) { userState.grantReason = 'METERING'}
        if (!(profile && profile.email)) {
            logger('info', `updateUserState user is anonymous, userState ${JSON.stringify(userState)}`)
        } else {
            userState.id = profile.uuid
            userState.registrationTimestamp = new Date(profile.created).getTime()
            if ( profile && profile.subscriptions && profile.adLight) {
                for (const subscription of profile.subscriptions) {
                    if (subscription.aid === this.pianoAid) {
                        userState.subscriptionTimestamp = subscription.start
                        userState.grantReason = 'SUBSCRIBER'
                        logger('info', `updateUserState user is subscribed, userState ${JSON.stringify(userState)}`)
                    }
                }
            }
        }
        if (localStorage) {
            localStorage.setItem(Store.GNS_USER_STATE, JSON.stringify(userState))
        } else {
            logger('warn', 'updateUserState localStorage is unavailable')
        }
        return userState
    }

    public readUserState(): IIExtendedAccessUserState {
        let userState: IIExtendedAccessUserState = { granted:true, grantReason: 'METERING'}
        if (localStorage) {
            userState = JSON.parse(localStorage.getItem(Store.GNS_USER_STATE)) || userState
        } else {
            logger('warn', 'updateUserState localStorage is unavailable')
            userState = { granted: true }  // ToDo: Is this fallback value correct if localStorage not available?
        }

        return userState
    }

    public async initGaaMetering(authorizationUrl?: string, allowedReferrers?: string[]) {
        // Integration per google extended access spec:
        // https://developers.google.com/news/subscribe/extended-access/reference/third-party-identity-integration

        const handleLoginPromise = new Promise(async (resolve) => {
            try {
                // Open the login page form or redirect the user to the login page
                await window.GaaMetering.getLoginPromise()
                    .then( () =>
                    // Open a Postmedia login window for existing users to login.
                    this.identityProvider.signIn()
                )
                logger('info', 'initGaaMetering getLoginPromise user logged in')
                // return updated userState
                const userState = this.readUserState()
                resolve(userState)
            } catch {
                logger('error', 'initGaaMetering getLoginPromise error')
            }
        })
        // Get userState by passing the gaaUser object to your registration endpoint server
        // Takes the registration JWT and returns an updated userState object for the newly registered user.
        // Not needed in our implementation since we refresh the entire page when the user registers
        const registerUserPromise = new Promise(() => {
            logger('info', 'gnsshowcase registerUserPromise')
        })
        const publisherEntitlementPromise = new Promise((resolve) => {
            const userState = this.readUserState()
            resolve(userState)
        })
        const param: IGaaMeteringParam = {
            allowedReferrers: allowedReferrers ? allowedReferrers : ['*'],
            authorizationUrl: authorizationUrl  ? authorizationUrl : '',
            handleLoginPromise,
            handleSwGEntitlement: this.handleSwGEntitlement.bind(this),
            publisherEntitlementPromise,
            registerUserPromise,
            showPaywall: this.showPaywall.bind(this),
            unlockArticle: this.unlockArticle.bind(this),
            userState: this.readUserState()
        }
        window.GaaMetering.init(param)
        logger('info', 'initGaaMetering is initialized')
    }

    public handleSwGEntitlement() {
        this.subService.closeCheckoutModal()
        logger('info', 'initGaaMetering handleSwGEntitlement')
    }

    public showPaywall() {
        // Show Piano Paywall
        if (this.subService && this.gnsOptions && this.gnsOptions.offerId && this.gnsOptions.templateId) {
            this.subService.closeCheckoutModal()
            this.subService.showPaywall(this.gnsOptions.offerId, this.gnsOptions.templateId)
        } else { logger('error', 'GNS showpaywall undefined offerId and templateId')}
        logger('info', 'GNS initGaaMetering showPaywall')
    }

    public unlockArticle() {
        // Unlock article
        if (this.subService) {
            this.subService.closeCheckoutModal()
            logger('info', 'GNS unlockArticle article unlocked')
        } else { logger('info', 'GNS unlockArticle - piano not ready') }
    }

    public isGnsEnabled(url: string): boolean {
        const locUrl = new URL(url)
        const params = new URLSearchParams(locUrl.search)
        return params.has('utm_source') && (params.get('utm_source') === 'newsshowcase') &&
                        params.has('utm_medium') && (params.get('utm_medium') === 'gnews') &&
                        params.has('gaa_at') && params.has('gaa_ts') && params.has('gaa_n') &&
                        params.has('gaa_sig')
    }

    public reloadIfNeeded(): void {
        // workaround when the page is missing GNS parameters after user logged in using safari token
        const reloadUrl = sessionStorage ? sessionStorage.getItem(Store.GNS_REDIRECT_URL) : undefined
        if (
            reloadUrl &&
            this.isGnsEnabled(reloadUrl) &&
            !this.isGnsEnabled(location.href) &&
            (location.href.replace(/\/$/, '') !== `${location.origin}/sign-in`)
        ) {
            sessionStorage.removeItem(Store.GNS_REDIRECT_URL)
            location.assign(reloadUrl)
        }
    }
}
